import { ModalTypeEnum, Table, useAlert, useFilters, useModal } from "@wit/mpesa-ui-components";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { BaseModalProps } from "@wit/mpesa-ui-components/lib/context/modal/modal.types";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ThemeContext } from "styled-components";
import { IStoreInterface } from "../../../../../configs/store.config";
import BundlesApi from "../../bundles.api";
import { IBundleType, IMiddlewareSubBundle } from "../../bundles.model";
import { filterMiddlewareSubBundles, renderSubBundlesListQuickActions, SubBundlesUtils } from "../sub-bundles.utils";
import MiddlewareSubBundlesListFilters from "./middleware-sub-bundles-list-filters.component";
import EditMiddlewareSubBundleModal from "./edit-middleware-sub-bundle.modal";
import { getDefaultTranslation } from "../../../../../shared/shared.utils";

interface IMiddlewareSubBundlesListInterface {
  bundle: IBundleType;
  reloadBundleDetails: () => void;
}

/**
 * Sub bundles list
 * @param param0
 */
const MiddlewareSubBundlesList = ({ bundle, reloadBundleDetails }: IMiddlewareSubBundlesListInterface) => {
  const [t] = useTranslation();
  const configurationFieldsStore = useSelector((state: IStoreInterface) => state.bundlesReducer.configurationFields);
  const [configurationFields, setConfigurationFields] = React.useState(configurationFieldsStore);
  const [selectedSubBundle, setSelectedSubBundle] = React.useState<IMiddlewareSubBundle>();
  const themeContext = useContext(ThemeContext);
  const {
    filters,
    updateFilter,
    isFilterActive,
    clearFilter,
    resetFilters,
    getFilterValue,
    getFiltersQueryString,
    updateMultipleFilters,
  } = useFilters();

  const [showableSubBundles, setShowableSubBundles] = React.useState<IMiddlewareSubBundle[]>(
    bundle.items ? (bundle.items as IMiddlewareSubBundle[]) : [],
  );
  const [showConfirmationModal, hideConfirmationModal, setConfirmationProps] = useModal(
    ModalTypeEnum.ConfirmationModal,
  );
  const [showAlert, , setAlertProps] = useAlert();

  /*
   * Filters effect
   * */
  React.useEffect(() => {
    if (filters.size > 0 && bundle.items) {
      setShowableSubBundles(filterMiddlewareSubBundles(bundle.items as IMiddlewareSubBundle[], filters));
    } else {
      setShowableSubBundles(bundle.items ? (bundle.items as IMiddlewareSubBundle[]) : []);
    }
  }, [filters, bundle]);

  /*
   * Configuration fields effect
   * */
  React.useEffect(() => {
    setConfigurationFields(configurationFieldsStore);
  }, [configurationFieldsStore]);

  /**
   * Delete sub-bundle
   * @param subBundle
   */
  const deleteMiddlewareSubBundle = (subBundle: IMiddlewareSubBundle) => {
    BundlesApi.methods
      .deleteSubBundle(bundle.id, subBundle.id)
      .then(
        () => {
          setAlertProps({
            title: t("pages.bundleDetails.deleteSubBundleSuccess"),
            type: AlertTypeEnum.SUCCESS,
          });
          showAlert();
          reloadBundleDetails();
        },
        () => {
          setAlertProps({
            title: t("pages.bundleDetails.deleteSubBundleError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
        },
      )
      .finally(() => {
        hideConfirmationModal();
      });
  };

  /**
   * Delete sub-bundle
   * @param subBundle
   */
  const restoreMiddlewareSubBundle = (subBundle: IMiddlewareSubBundle) => {
    BundlesApi.methods
      .restoreSubBundle(bundle.id, subBundle.id)
      .then(
        () => {
          setAlertProps({
            title: t("pages.bundleDetails.restoreSubBundleSuccess"),
            type: AlertTypeEnum.SUCCESS,
          });
          showAlert();
          reloadBundleDetails();
        },
        () => {
          setAlertProps({
            title: t("pages.bundleDetails.restoreSubBundleError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
        },
      )
      .finally(() => {
        hideConfirmationModal();
      });
  };

  /**
   * Delete sub-bundle handler
   * @param subBundle
   */
  const showDeleteConfirmation = (subBundle: IMiddlewareSubBundle) => {
    setConfirmationProps({
      title: t("pages.bundleDetails.deleteSubBundleTitle"),
      description: t("pages.bundleDetails.deleteSubBundleConfirmation"),
      primaryBtnId: "confirm-button",
      secondaryBtnId: "cancel-button",
      primaryAction: () => deleteMiddlewareSubBundle(subBundle),
      secondaryAction: () => hideConfirmationModal(),
    } as BaseModalProps);
    showConfirmationModal();
  };

  /**
   * Restore sub-bundle handler
   */
  const showRestoreConfirmation = (subBundle: IMiddlewareSubBundle) => {
    setConfirmationProps({
      title: t("pages.bundleDetails.restoreSubBundleTitle"),
      description: t("pages.bundleDetails.restoreSubBundleConfirmation"),
      primaryAction: () => restoreMiddlewareSubBundle(subBundle),
      secondaryAction: () => hideConfirmationModal(),
    } as BaseModalProps);
    showConfirmationModal();
  };

  /**
   * Edit sub-bundle modal
   */
  const _hideEditMiddlewareSubBundleModal = () => {
    hideEditMiddlewareSubBundleModal();
  };
  const [editMiddlewareSubBundleModal, hideEditMiddlewareSubBundleModal, editMiddlewareSubBundleModalProps] = useModal(
    ModalTypeEnum.CustomModal,
    undefined,
    <EditMiddlewareSubBundleModal
      subBundle={selectedSubBundle as IMiddlewareSubBundle}
      bundle={bundle}
      configurationFields={configurationFields}
      dismissModal={_hideEditMiddlewareSubBundleModal}
      reloadSubBundles={reloadBundleDetails}
    />,
  );
  React.useEffect(() => {
    editMiddlewareSubBundleModalProps({ modalStyles: { width: 825, padding: 0 } });
  }, [editMiddlewareSubBundleModalProps]);

  /**
   * Opens the edit sub-bundle modal
   * @param s
   */
  const showEditMiddlewareSubBundleModal = (s: IMiddlewareSubBundle) => {
    setSelectedSubBundle(s);
    editMiddlewareSubBundleModal();
  };
  return (
    <>
      <MiddlewareSubBundlesListFilters
        filters={filters}
        isFilterActive={isFilterActive}
        updateFilter={updateFilter}
        clearFilter={clearFilter}
        resetFilters={resetFilters}
        getFilterValue={getFilterValue}
        getFiltersQueryString={getFiltersQueryString}
        updateMultipleFilters={updateMultipleFilters}
        configurationFields={configurationFields}
        bundle={bundle}
        reloadSubBundles={reloadBundleDetails}
      ></MiddlewareSubBundlesListFilters>

      <Table<IMiddlewareSubBundle>
        values={showableSubBundles}
        displayActions={subBundle =>
          renderSubBundlesListQuickActions<IMiddlewareSubBundle>(
            subBundle,
            {
              edit: { onClick: showEditMiddlewareSubBundleModal },
              delete: { onClick: showDeleteConfirmation },
              restore: { onClick: showRestoreConfirmation },
            },
            themeContext,
            subBundle.name && subBundle.name.translations ? getDefaultTranslation(subBundle.name.translations) : "",
          )
        }
        columns={SubBundlesUtils.getMiddlewareSubBundlesListColumns(configurationFields)}
      />
    </>
  );
};

export default MiddlewareSubBundlesList;
